import React from "react"
import { useId } from "@reach/auto-id"
import { isNotEmpty } from "utils/validation"
import { useTranslation } from "react-i18next"

import { handleInternalInputValidation, handlePreventTabsValueAtEndOnInput } from "../formUtils"
import { Errors } from "../Errors"

export const InputNumber = ({
    // Attributes
    id,
    value = "",
    name,
    label = "",
    placeholder = "",
    unit = "",
    disabled,
    readonly = false,
    // Styles
    style,
    className = "",
    classNameLabel = "",
    classNameInput = "",
    // Error management
    required = false,
    customRules,
    showErrors,
    // Handler
    onChange,
    onValidate,
    ...rest
}) => {
    const inputRef = React.useRef(null)
    const [errors, setErrors] = React.useState([])
    const { t } = useTranslation()

    const prefix = useId()
    const inputId = id ?? `${prefix}-${name}`
    const errorId = `${inputId}-error`

    const rules = React.useMemo(() => [
        {
            key: "input-empty",
            check: (value) => required ? isNotEmpty(value) : true,
            message: t("error_mandatory_field", { ns: "form" })
        },
        ...(customRules || [])
    ], [required, t]) // eslint-disable-line
    // customRules in dependencies array trigger an infinite loop,
    // because custom rules include a check property defining a new function,
    // preventing useMemo to return a valid shallow-comparison
    // or event useDeepMemo to return a valid deep-comparison

    const validate = React.useCallback(
        (currentValue) =>
            handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
        [rules, name, onValidate]
    )

    // validate on mount and each time value change
    React.useEffect(() => {
        validate(value)
        return () => {
            // On unmounted erros and validation are no longer needed
            setErrors([])
            if (onValidate) onValidate(name, [])
        }
    }, [value, validate, onValidate, name])

    return (
        <>
            <div className={"c-form-group " + className} style={style}>
                <label className={"c-label mb-1 " + classNameLabel} htmlFor={inputId}>{label} {required && "*"}</label>

                <div className="flex items-baseline">
                    <input
                        ref={inputRef}
                        className={
                            "c-input "
                            + (showErrors && errors.length > 0 ? "has-error " : " ")
                            + (classNameInput ? classNameInput : "")
                        }
                        id={inputId}
                        name={name}
                        type={"number"}
                        placeholder={placeholder}
                        readOnly={readonly}
                        value={value}
                        disabled={disabled}
                        required={required}
                        onChange={(e) => !readonly && onChange(handlePreventTabsValueAtEndOnInput(e))}
                        onBlur={() => validate(value)}
                        // Disable Up and Down Arrows on Keyboard
                        onWheel={() => inputRef.current.blur()}
                        onKeyDown={(e) => {
                            const keyCode = e.keyCode || e.which
                            if (keyCode === 38 || keyCode === 40) {
                                e.preventDefault()
                            } else {
                                return
                            }
                            
                        }}
                        aria-describedby={errorId}
                        aria-invalid={Boolean(errors.length)}
                        {...rest}
                    />
                    {unit && <span className="ml-2">{unit}</span>}
                </div>

                {/* Error messages */}
				{showErrors && <Errors id={errorId} errors={errors} />}
            </div>
        </>
    )
}
